import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef
} from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInputCustom/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import TagsContainerSkeleton from "../TagsContainerSkeleton";
import TicketValueField from "../TicketValueField";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import { shouldDisplayFlowbuilderErrorToast } from "../../utils/flowbuilderErrorToast";
import { CommercialContextProvider } from "../../context/Commercial/CommercialContext";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%", // 👈 Usa 100% do container pai
    position: "relative",
    overflow: "hidden",
    // The contact panel and its blur open inside this box on a tablet, so it follows the box's corners.
    'html[data-page-header="tablet"] &': { borderRadius: "0 12px 12px 0" },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // 👈 Importante: sem scroll aqui
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.neoUI.effects.glow.main,
    border: theme.neoUI.effects.contour.main,
    // No celular a barra superior já traz o contorno; os dois juntos formavam uma faixa.
    "html[data-phone-navigation] &": { borderTop: "none" },
    borderRight: "none", // 👈 Remove borda direita para colar no TicketsManager
    borderLeft: "none", // 👈 Remove borda esquerda para colar no TicketsManager
    // Controle de overscroll para desktop
    [theme.breakpoints.up('md')]: {
      overscrollBehavior: "contain",
    },
    borderTopLeftRadius: 0,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 12,
    [theme.breakpoints.down("sm")]: {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      marginRight: 0, // 👈 Never shift content in mobile when Drawer overlaps as 'temporary'
    },
    // iPad/tablet touch: ContactDrawer usa variant="temporary" (coarse pointer + md screen),
    // portanto não há drawer inline — o marginRight negativo causaria overflow horizontal.
    "@media (max-width: 1279.95px) and (pointer: coarse)": {
      marginRight: 0,
    },
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    minHeight: 0,
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  tagsPaper: {
    "html[data-phone-navigation] &": { backgroundColor: theme.mode === "dark" ? "#1c1c1c" : theme.palette.background.paper },
    border: "none",
    boxShadow: "none",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    padding: "2px 12px 6px 12px",
    boxSizing: "border-box",
    flex: "0 0 auto",
  },

  messagesSection: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  tagsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
  },

  tagsWrapper: {
    flex: 1,
    minWidth: 0,
  },

  valueWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    // minWidth: 180,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const ignoreNextDeleteRef = useRef(Boolean(location.state?.createdFromModal));
  const mainWrapperRef = useRef(null);

  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputResetVersion, setInputResetVersion] = useState(0);
  // Responsive remounts and notifications can carry only a ticket locator.
  // Keep the loading state until its contact is hydrated for the current route.
  const preloadedTicket = useMemo(() => {
    const cached = location.state?.ticket;
    if (!cached?.id || !cached?.contact?.id) return null;
    const matchesRoute = String(cached.id) === String(ticketId) || cached.uuid === ticketId;
    return matchesRoute ? cached : null;
  }, [location.state?.ticket, ticketId]);
  const [loading, setLoading] = useState(!preloadedTicket);
  const [contact, setContact] = useState(preloadedTicket?.contact || {});
  const [ticket, setTicket] = useState(preloadedTicket || {});
  const [pendingJumpTarget, setPendingJumpTarget] = useState(() => {
    const state = location.state || {};
    if (!state.jumpToMessageId) {
      return null;
    }

    return {
      messageId: state.jumpToMessageId,
      pageNumber: state.jumpPageNumber || null,
      origin: state.jumpOrigin || "",
      searchTerm: state.jumpSearchTerm || "",
      requestKey: `${location.key || "jump"}-${String(state.jumpToMessageId)}`,
    };
  });
  const openedFromKanbanMobilePreview = Boolean(
    location.state?.fromKanbanMobilePreview
  );
  const kanbanReturnState = location.state?.kanbanReturn || null;

  useEffect(() => {
    const state = location.state || {};
    const hasCreatedFromModal = Boolean(state.createdFromModal);
    const hasJumpState = Boolean(state.jumpToMessageId);
    if (!hasCreatedFromModal && !hasJumpState) {
      return;
    }

    if (state.jumpToMessageId) {
      setPendingJumpTarget({
        messageId: state.jumpToMessageId,
        pageNumber: state.jumpPageNumber || null,
        origin: state.jumpOrigin || "",
        searchTerm: state.jumpSearchTerm || "",
        requestKey: `${location.key || "jump"}-${String(state.jumpToMessageId)}`,
      });
    }

    const {
      createdFromModal: _createdFromModal,
      jumpToMessageId: _jumpToMessageId,
      jumpPageNumber: _jumpPageNumber,
      jumpSearchTerm: _jumpSearchTerm,
      jumpOrigin: _jumpOrigin,
      ...restState
    } = state;

    // IMPORTANTE: Manter os parâmetros de query intactos para nao quebrar background selections e outras rules.
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: restState
    });
  }, [history, location.pathname, location.search, location.state, location.key]);

  const handleJumpHandled = useCallback(() => {
    setPendingJumpTarget(null);
  }, []);

  const handleBackNavigation = useCallback(() => {
    if (openedFromKanbanMobilePreview) {
      history.push("/kanban", {
        restoreKanbanContext: true,
        boardId: kanbanReturnState?.boardId ?? null,
        searchParam: kanbanReturnState?.searchParam ?? "",
        selectedTags: Array.isArray(kanbanReturnState?.selectedTags)
          ? kanbanReturnState.selectedTags
          : undefined,
        selectedUserId: kanbanReturnState?.selectedUserId ?? null,
      });
      return;
    }

    history.push("/tickets");
  }, [history, openedFromKanbanMobilePreview, kanbanReturnState]);

  // Detectar modo readonly baseado no ticket carregado + perfil do usuário
  const isReadonly = useMemo(() => {
    const normalizedProfile = (user?.profile || "").toUpperCase();
    const canViewPendingTickets = user?.viewPendingTickets === "enabled";

    if (ticket?.status === "pending" && ticket?.userId === user?.id) {
      return false;
    }

    const readonly =
      ticket?.status === "pending" &&
      (normalizedProfile === "ADMIN" || canViewPendingTickets) &&
      ticket?.userId !== user?.id;

    return readonly;
  }, [
    ticket?.status,
    ticket?.userId,
    user?.profile,
    user?.viewPendingTickets,
    user?.id
  ]);

  const handleTicketPatched = useCallback((updatedTicket) => {
    if (!updatedTicket) {
      return;
    }

    setTicket(prevTicket => {
      if (!prevTicket?.id) {
        return { ...prevTicket, ...updatedTicket };
      }

      if (!updatedTicket.id) {
        return { ...prevTicket, ...updatedTicket };
      }

      // Preserva contact do estado anterior se não vier no update
      // Isso previne race condition onde Socket.IO envia ticket sem contact
      return {
        ...prevTicket,
        ...updatedTicket,
        contact: updatedTicket.contact || prevTicket.contact
      };
    });

    if (updatedTicket.contact) {
      setContact(updatedTicket.contact);
    }

    if (updatedTicket.id) {
      window.dispatchEvent(
        new CustomEvent("ticket:patched", {
          detail: { ticket: updatedTicket }
        })
      );
    }
  }, []);

  const socketManager = useContext(SocketContext);

  const fetchTicket = useCallback(async () => {
    const hasPreloadedTicket = Boolean(preloadedTicket);
    if (!hasPreloadedTicket) {
      setLoading(true);
    }
    try {
      // Determina endpoint baseado no formato do identificador
      const isNumericId = /^\d+$/.test(ticketId);
      const endpoint = isNumericId ? `/tickets/${ticketId}` : `/tickets/u/${ticketId}`;

      const { data } = await api.get(endpoint);
      const { queueId } = data;
      const { queues = [], profile, allowNoQueue } = user;

      const queueAllowed = queues.find((q) => q.id === queueId);
      const isTicketWithoutQueue = queueId === null || queueId === undefined;
      const canAccessTicket =
        profile === "admin" ||
        user.allTicket === "enabled" ||
        queueAllowed !== undefined ||
        (isTicketWithoutQueue && allowNoQueue);

      if (!canAccessTicket) {
        toast.error(i18n.t("tickets.toasts.unauthorized"));
        history.push("/tickets");
        return;
      }

      // Garante que ticket sempre tenha contact antes de atualizar estado
      // Isso previne race condition onde ticket.contact pode ficar undefined
      const ticketWithContact = {
        ...data,
        contact: data.contact // Garante que contact está presente
      };

      setContact(data.contact);
      setTicket(ticketWithContact);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  }, [ticketId, user, history, preloadedTicket]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    // Usa ticketId do URL como fallback para evitar race condition
    // quando ticket?.id ainda não foi carregado da API
    const currentTicketId = ticket?.id || (ticketId && /^\d+$/.test(ticketId) ? Number(ticketId) : null);

    const handleReady = () => {
      if (currentTicketId) {
        socket.emit("joinChatBox", `${currentTicketId}`);
      }
    };

    socket.on("ready", handleReady);

    if (currentTicketId) {
      socket.emit("joinChatBox", `${currentTicketId}`);
    }

    const ticketChannel = `company-${companyId}-ticket`;
    const handleTicketEvent = (data) => {
      if (data.action === "update" && data.ticket.id === currentTicketId) {
        handleTicketPatched(data.ticket);
      }

      if (data.action === "flowbuilderError" && data.ticketId === currentTicketId) {
        if (shouldDisplayFlowbuilderErrorToast(data.eventId)) {
          toast.error(
            data.message ||
            "Não foi possível transferir: já existe outro ticket nessa conexão"
          );
        }
      }

      if (data.action === "delete" && data.ticketId === currentTicketId) {
        if (ignoreNextDeleteRef.current) {
          ignoreNextDeleteRef.current = false;
          return;
        }
        history.push("/tickets");
      }
    };
    socket.on(ticketChannel, handleTicketEvent);

    const contactChannel = `company-${companyId}-contact`;
    const handleContactEvent = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };
    socket.on(contactChannel, handleContactEvent);

    const taskChannel = `company-${companyId}-task`;
    const handleTaskEvent = (data) => {
      if (!data) return;
      const { action } = data;

      if (action === "update" || action === "create") {
        const task = data.task;
        if (task && String(task.ticketId) === String(currentTicketId)) {
          setTicket((prev) => {
            const currentTasks = Array.isArray(prev.tasks) ? prev.tasks : [];
            const taskIndex = currentTasks.findIndex((t) => t.id === task.id);
            let nextTasks;
            if (taskIndex !== -1) {
              nextTasks = [...currentTasks];
              nextTasks[taskIndex] = task;
            } else {
              nextTasks = [...currentTasks, task];
            }
            return { ...prev, tasks: nextTasks };
          });
        }
      }

      if (action === "delete") {
        const { taskId, ticketId } = data;
        if (String(ticketId) === String(currentTicketId)) {
          setTicket((prev) => {
            const currentTasks = Array.isArray(prev.tasks) ? prev.tasks : [];
            const nextTasks = currentTasks.filter((t) => t.id !== taskId);
            return { ...prev, tasks: nextTasks };
          });
        }
      }
    };
    socket.on(taskChannel, handleTaskEvent);

    return () => {
      if (currentTicketId) {
        socket.emit("leaveChatBox", `${currentTicketId}`);
      }
      socket.off("ready", handleReady);
      socket.off(ticketChannel, handleTicketEvent);
      socket.off(contactChannel, handleContactEvent);
      socket.off(taskChannel, handleTaskEvent);
    };
  }, [ticketId, ticket?.id, history, socketManager, handleTicketPatched]);

  useEffect(() => {
    const mainElement = mainWrapperRef.current;
    if (!mainElement || typeof window === "undefined") {
      return undefined;
    }

    const handleTransitionEnd = (event) => {
      if (event?.propertyName !== "margin-right" && event?.propertyName !== "margin") {
        return;
      }
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };

    mainElement.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      mainElement.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, []);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setInputResetVersion((version) => version + 1);
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
          onBack={handleBackNavigation}
        />
      );
    }
  };

  const resolvedTicketId = useMemo(() => {
    if (ticket?.id) {
      return ticket.id;
    }

    if (!ticketId) {
      return undefined;
    }

    const numericId = Number(ticketId);
    if (Number.isFinite(numericId) && numericId > 0) {
      return numericId;
    }

    return undefined;
  }, [ticket?.id, ticketId]);

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          ticket={ticket}
          ticketId={resolvedTicketId}
          isGroup={ticket.isGroup}
          readonly={isReadonly}
          jumpToMessageId={pendingJumpTarget?.messageId}
          jumpPageNumber={pendingJumpTarget?.pageNumber}
          jumpOrigin={pendingJumpTarget?.origin}
          jumpSearchTerm={pendingJumpTarget?.searchTerm}
          jumpRequestKey={pendingJumpTarget?.requestKey}
          onJumpHandled={handleJumpHandled}
        ></MessagesList>
        <MessageInput
          ticketId={resolvedTicketId}
          isGroup={ticket.isGroup}
          ticketStatus={ticket.status}
          contactId={contact?.id}
          readonly={isReadonly}
          inputResetVersion={inputResetVersion}
        />
      </>
    );
  };

  return (
    <CommercialContextProvider ticketId={resolvedTicketId} initialCardId={location.state?.commercialCardId} readonly={isReadonly} allowAcceptPendingPreview>
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        ref={mainWrapperRef}
        data-ticket-window
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          {renderTicketInfo()}
          <TicketActionButtons ticket={ticket} />
        </TicketHeader>
        {loading ? (
          <TagsContainerSkeleton />
        ) : (
          <Paper className={classes.tagsPaper} elevation={0}>
            <div className={classes.tagsRow}>
              <div className={classes.tagsWrapper}>
                <TagsContainer ticket={ticket} onTicketUpdate={handleTicketPatched} />
              </div>
              <div className={classes.valueWrapper}>
                <TicketValueField
                  ticketId={ticket?.id}
                  value={ticket?.value}
                  disabled={!ticket?.id || loading}
                  onTicketUpdate={handleTicketPatched}
                />
              </div>
            </div>
          </Paper>
        )}
        <div className={classes.messagesSection}>
          <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
        </div>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
        onContactUpdated={handleTicketPatched}
      />
    </div>
    </CommercialContextProvider>
  );
};

export default Ticket;
